import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { MAX_VISIBLE_VIDEOS, useTrendingVideo } from '../../../../apps/hooks/video/useTrendingVideo';
import type { TrendingVideo, UpdateTrendingVideoPayload } from '../../../../infrastructure/TrendingVideoClient';
import { ROUTES } from '../../../../routes/routes';
import EditTrendingVideoModal from './components/EditTrendingVideoModal';
import TrendingVideoOrderModal from './components/TrendingVideoOrderModal';

const isSafeUrl = (url?: string): url is string => typeof url === 'string' && /^https?:\/\//i.test(url);

const sortVideosForList = (videos: TrendingVideo[]): TrendingVideo[] =>
  [...videos].sort((a, b) => {
    if (a.isVisible !== b.isVisible) {
      return a.isVisible ? -1 : 1;
    }
    return a.order - b.order;
  });

export default function TrendingVideoListPage() {
  const trendingVideo = useTrendingVideo();

  const [videos, setVideos] = useState<TrendingVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>('all');

  const [editing, setEditing] = useState<TrendingVideo | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await trendingVideo.list({ limit: 100 });
      setVideos(sortVideosForList(response.videos));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trending videos');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [trendingVideo.list]);

  useEffect(() => {
    void fetchVideos();
  }, [fetchVideos]);

  const filtered = videos.filter((video) => {
    if (visibilityFilter === 'visible') {
      return video.isVisible;
    }
    if (visibilityFilter === 'hidden') {
      return !video.isVisible;
    }
    return true;
  });

  const visibleCount = videos.filter((video) => video.isVisible).length;

  const handleDelete = async (video: TrendingVideo) => {
    if (!window.confirm(`Delete trending video for "${video.productFocus}"? This also removes the mp4 file.`)) {
      return;
    }
    try {
      await trendingVideo.remove(video.videoId);
      await fetchVideos();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete trending video');
    }
  };

  const handleToggleVisibility = async (video: TrendingVideo) => {
    const willHide = video.isVisible;
    const willShow = !video.isVisible;

    // Block showing when already at cap
    if (willShow && visibleCount >= MAX_VISIBLE_VIDEOS) {
      toast.error(`Maximum ${MAX_VISIBLE_VIDEOS} visible videos reached. Hide another video first.`);
      return;
    }

    // Warn when hiding the last visible video
    if (willHide && visibleCount === 1) {
      const proceed = window.confirm('Hiding this video will leave the Trending Now section empty. Continue?');
      if (!proceed) {
        return;
      }
    }

    try {
      // Toggle the video's visibility
      const updated = await trendingVideo.update(video.videoId, { isVisible: !video.isVisible });
      const updatedList = videos.map((item) => (item.videoId === video.videoId ? updated : item));

      // Recompute order: visible videos get 1..N, hidden videos get N+1..
      const visibleVideos = updatedList.filter((v) => v.isVisible).sort((a, b) => a.order - b.order);
      const hiddenVideos = updatedList.filter((v) => !v.isVisible).sort((a, b) => a.order - b.order);

      const orderUpdates: Array<{ videoId: string; order: number }> = [];
      visibleVideos.forEach((v, i) => {
        const newOrder = i + 1;
        if (v.order !== newOrder) {
          orderUpdates.push({ videoId: v.videoId, order: newOrder });
        }
      });
      hiddenVideos.forEach((v, i) => {
        const newOrder = visibleVideos.length + i + 1;
        if (v.order !== newOrder) {
          orderUpdates.push({ videoId: v.videoId, order: newOrder });
        }
      });

      if (orderUpdates.length > 0) {
        await trendingVideo.updateOrders(orderUpdates);
      }

      // Update local state instead of full re-fetch
      const reorderedVisible = visibleVideos.map((v, i) => ({ ...v, order: i + 1 }));
      const reorderedHidden = hiddenVideos.map((v, i) => ({ ...v, order: visibleVideos.length + i + 1 }));
      setVideos([...reorderedVisible, ...reorderedHidden]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update visibility');
      await fetchVideos();
    }
  };

  const handleEdit = (video: TrendingVideo) => {
    setEditing(video);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (videoId: string, patch: UpdateTrendingVideoPayload) => {
    try {
      const updated = await trendingVideo.update(videoId, patch);
      setVideos((prev) => prev.map((item) => (item.videoId === videoId ? updated : item)));
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update trending video');
    }
  };

  const handleSaveOrder = async (updates: Array<{ videoId: string; order: number }>) => {
    setIsUpdatingOrder(true);
    try {
      await trendingVideo.updateOrders(updates);
      const orderByVideoId = new Map(updates.map((update) => [update.videoId, update.order]));
      setVideos((prev) =>
        sortVideosForList(
          prev.map((video) => {
            const updatedOrder = orderByVideoId.get(video.videoId);
            return updatedOrder === undefined ? video : { ...video, order: updatedOrder };
          }),
        ),
      );
      setShowOrderModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update trending video order');
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Trending Videos ({videos.length})</h2>
        <div className="d-flex gap-2">
          <Button variant="success" as="a" href={ROUTES.trendingVideo.upload}>
            Upload New Video
          </Button>
          <Button variant="info" onClick={() => setShowOrderModal(true)} disabled={visibleCount === 0}>
            Manage Order
          </Button>
          <Button variant="primary" onClick={() => void fetchVideos()}>
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {visibleCount > MAX_VISIBLE_VIDEOS && (
        <Alert variant="warning" className="mb-3">
          {visibleCount} videos are currently visible — the homepage is designed to show at most {MAX_VISIBLE_VIDEOS}.
          Consider hiding some before the carousel gets crowded.
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body className="py-2">
          <Form.Group className="d-flex align-items-center gap-3 mb-0">
            <Form.Label className="mb-0">Filter:</Form.Label>
            <Form.Check
              type="radio"
              id="filter-all"
              label={`All (${videos.length})`}
              checked={visibilityFilter === 'all'}
              onChange={() => setVisibilityFilter('all')}
            />
            <Form.Check
              type="radio"
              id="filter-visible"
              label={`Visible (${visibleCount})`}
              checked={visibilityFilter === 'visible'}
              onChange={() => setVisibilityFilter('visible')}
            />
            <Form.Check
              type="radio"
              id="filter-hidden"
              label={`Hidden (${videos.length - visibleCount})`}
              checked={visibilityFilter === 'hidden'}
              onChange={() => setVisibilityFilter('hidden')}
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">Loading…</div>
          ) : videos.length === 0 ? (
            <div className="text-center py-4">
              <p className="mb-3">No trending videos yet.</p>
              <Button as="a" variant="primary" href={ROUTES.trendingVideo.upload}>
                Add your first video
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-4 text-muted">No videos match the current filter.</div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Order</th>
                  <th style={{ width: '160px' }}>Preview</th>
                  <th>Focus label</th>
                  <th>Product slug</th>
                  <th style={{ width: '120px' }}>Visible</th>
                  <th style={{ width: '220px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((video) => (
                  <tr key={video.videoId}>
                    <td>
                      {video.isVisible ? <Badge bg="primary">#{video.order}</Badge> : <Badge bg="secondary">—</Badge>}
                    </td>
                    <td>
                      <video
                        src={video.videoUrl}
                        preload="metadata"
                        muted
                        style={{ width: '140px', height: '78px', objectFit: 'cover', background: '#000' }}
                        className="rounded"
                      />
                    </td>
                    <td>
                      <div className="fw-bold">{video.productFocus}</div>
                      {isSafeUrl(video.contentLink) && (
                        <a
                          href={video.contentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="small text-muted"
                        >
                          Source
                        </a>
                      )}
                    </td>
                    <td>
                      {isSafeUrl(video.productLink) ? (
                        <a href={video.productLink} target="_blank" rel="noopener noreferrer">
                          {video.productSlug}
                        </a>
                      ) : (
                        <span>{video.productSlug}</span>
                      )}
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        id={`visible-${video.videoId}`}
                        checked={video.isVisible}
                        onChange={() => void handleToggleVisibility(video)}
                        label={video.isVisible ? 'Visible' : 'Hidden'}
                      />
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button size="sm" variant="outline-primary" onClick={() => handleEdit(video)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => void handleDelete(video)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <EditTrendingVideoModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditing(null);
        }}
        video={editing}
        onSave={handleSaveEdit}
      />

      <TrendingVideoOrderModal
        show={showOrderModal}
        onHide={() => setShowOrderModal(false)}
        videos={videos.filter((v) => v.isVisible)}
        onSave={handleSaveOrder}
        isLoading={isUpdatingOrder}
      />
    </div>
  );
}
